import { useState } from 'react';
import { Check, X, AlertCircle, Sparkles, Download, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { renderResumeDocx } from '@/renderers/resumeDocx';

interface Improvement {
  section: string;
  original: string;
  suggested: string;
  reason: string;
  priority: 'high' | 'medium' | 'low';
}

interface Suggestions {
  overall_score: number;
  improvements: Improvement[];
  missing_elements: string[];
  ats_keywords: string[];
  estimated_new_score: number;
}

interface ResumeEnhancementReviewProps {
  uploadedResumeId: string;
  suggestions: Suggestions;
  onComplete: () => void;
  onCancel: () => void;
}

export function ResumeEnhancementReview({
  uploadedResumeId,
  suggestions,
  onComplete,
  onCancel
}: ResumeEnhancementReviewProps) {
  const [approvedImprovements, setApprovedImprovements] = useState<Set<number>>(new Set());
  const [autoApprove, setAutoApprove] = useState(false);
  const [saving, setSaving] = useState(false);

  const toggleImprovement = (index: number) => {
    const newApproved = new Set(approvedImprovements);
    if (newApproved.has(index)) {
      newApproved.delete(index);
    } else {
      newApproved.add(index);
    }
    setApprovedImprovements(newApproved);
  };

  const handleApproveAll = () => {
    setApprovedImprovements(new Set(suggestions.improvements.map((_, i) => i)));
  };

  const handleRejectAll = () => {
    setApprovedImprovements(new Set());
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get original resume
      const { data: originalResume, error: fetchError } = await supabase
        .from('uploaded_resumes')
        .select('*')
        .eq('id', uploadedResumeId)
        .single();

      if (fetchError) throw fetchError;

      // Apply approved improvements
      const enhancedContent = JSON.parse(JSON.stringify(originalResume.parsed_data));
      const appliedImprovements: Improvement[] = [];

      suggestions.improvements.forEach((improvement, index) => {
        if (approvedImprovements.has(index)) {
          appliedImprovements.push(improvement);
          // Apply the improvement to the content
          // This is simplified - in reality you'd update specific fields
          if (improvement.section === 'Summary') {
            enhancedContent.headline = improvement.suggested;
          } else if (improvement.section === 'Experience') {
            // Find and update experience entry
            const expIndex = enhancedContent.experiences?.findIndex((exp: any) => 
              JSON.stringify(exp).includes(improvement.original)
            );
            if (expIndex !== -1 && enhancedContent.experiences) {
              enhancedContent.experiences[expIndex].achievements = 
                enhancedContent.experiences[expIndex].achievements.map((ach: string) =>
                  ach === improvement.original ? improvement.suggested : ach
                );
            }
          }
        }
      });

      // Save to optimized_resumes table
      const { data: optimizedResume, error: insertError } = await supabase
        .from('optimized_resumes')
        .insert([{
          user_id: user.id,
          source_type: 'upload',
          source_resume_id: uploadedResumeId,
          original_content: originalResume.parsed_data as any,
          optimized_content: enhancedContent as any,
          improvements: {
            applied: appliedImprovements,
            rejected: suggestions.improvements.filter((_: any, i: number) => !approvedImprovements.has(i)),
            missing_elements: suggestions.missing_elements,
            ats_keywords: suggestions.ats_keywords
          } as any,
          ats_score_before: suggestions.overall_score,
          ats_score_after: Math.round(
            suggestions.overall_score + 
            (suggestions.estimated_new_score - suggestions.overall_score) * 
            (approvedImprovements.size / suggestions.improvements.length)
          ),
          version_name: `${originalResume.file_name.replace(/\.[^/.]+$/, '')} - Enhanced`,
          optimization_level: 'moderate',
          keywords_added: suggestions.ats_keywords,
          credits_used: 2
        }])
        .select()
        .single();

      if (insertError) throw insertError;

      toast.success('Resume enhanced and saved!', {
        description: `Applied ${approvedImprovements.size} improvements. Find it in your Resume Library.`
      });

      onComplete();
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save enhanced resume', {
        description: error instanceof Error ? error.message : 'Please try again'
      });
    } finally {
      setSaving(false);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-500/10 text-red-500 border-red-500/20';
      case 'medium': return 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20';
      case 'low': return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
      default: return '';
    }
  };

  const scoreImprovement = Math.round(
    suggestions.overall_score + 
    (suggestions.estimated_new_score - suggestions.overall_score) * 
    (approvedImprovements.size / suggestions.improvements.length)
  );

  return (
    <div className="space-y-6">
      {/* Score Overview */}
      <Card className="bg-gradient-to-br from-primary/5 to-accent/5 border-primary/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            AI Resume Enhancement
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Current Score</p>
              <p className="text-3xl font-bold">{suggestions.overall_score}/100</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-muted-foreground">→</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Potential Score</p>
              <p className="text-3xl font-bold text-primary">{scoreImprovement}/100</p>
            </div>
          </div>
          <Progress value={(scoreImprovement / 100) * 100} className="h-2" />
          
          {suggestions.missing_elements.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium">Missing Elements:</p>
              <div className="flex flex-wrap gap-2">
                {suggestions.missing_elements.map((element, i) => (
                  <Badge key={i} variant="outline">
                    <AlertCircle className="h-3 w-3 mr-1" />
                    {element}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Auto-approve toggle */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="auto-approve">Auto-approve all suggestions</Label>
              <p className="text-sm text-muted-foreground">
                Automatically accept all AI improvements without review
              </p>
            </div>
            <Switch
              id="auto-approve"
              checked={autoApprove}
              onCheckedChange={(checked) => {
                setAutoApprove(checked);
                if (checked) {
                  handleApproveAll();
                }
              }}
            />
          </div>
        </CardContent>
      </Card>

      {/* Improvements List */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">
            Suggested Improvements ({approvedImprovements.size}/{suggestions.improvements.length} approved)
          </h3>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={handleApproveAll}>
              Approve All
            </Button>
            <Button size="sm" variant="outline" onClick={handleRejectAll}>
              Reject All
            </Button>
          </div>
        </div>

        {suggestions.improvements.map((improvement, index) => (
          <Card key={index} className={approvedImprovements.has(index) ? 'border-primary' : ''}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <CardTitle className="text-base">{improvement.section}</CardTitle>
                    <Badge className={getPriorityColor(improvement.priority)}>
                      {improvement.priority}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">{improvement.reason}</p>
                </div>
                <Button
                  size="sm"
                  variant={approvedImprovements.has(index) ? 'default' : 'outline'}
                  onClick={() => toggleImprovement(index)}
                >
                  {approvedImprovements.has(index) ? (
                    <>
                      <Check className="h-4 w-4 mr-1" />
                      Approved
                    </>
                  ) : (
                    <>
                      <X className="h-4 w-4 mr-1" />
                      Approve
                    </>
                  )}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Original:</p>
                <p className="text-sm bg-muted/50 p-3 rounded">{improvement.original}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-primary mb-1">Suggested:</p>
                <p className="text-sm bg-primary/5 p-3 rounded border border-primary/20">
                  {improvement.suggested}
                </p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 sticky bottom-0 bg-background pt-4 pb-2 border-t">
        <Button variant="outline" onClick={onCancel} className="flex-1">
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          disabled={saving || approvedImprovements.size === 0}
          className="flex-1"
        >
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Download className="h-4 w-4 mr-2" />
              Save Enhanced Resume
            </>
          )}
        </Button>
      </div>
    </div>
  );
}